import { useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import API_URL from '../config';

// Support Vite (import.meta.env) OU Create React App (process.env.REACT_APP_API_URL)
const API_BASE = API_URL;

function AddShiftsRequests() {
  const { user, isAuthenticated, token } = useAuth();
  const authToken = token || localStorage.getItem('token');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [requests, setRequests] = useState([]);

  // Form state (shift)
  const [shiftUserId, setShiftUserId] = useState('');
  const [shiftStartDate, setShiftStartDate] = useState('');
  const [shiftStartTime, setShiftStartTime] = useState('');
  const [shiftEndDate, setShiftEndDate] = useState('');
  const [shiftEndTime, setShiftEndTime] = useState('');

  // Form state (request)
  const [requestShiftId, setRequestShiftId] = useState('');
  const [reqStartDate, setReqStartDate] = useState('');
  const [reqStartTime, setReqStartTime] = useState('');
  const [reqEndDate, setReqEndDate] = useState('');
  const [reqEndTime, setReqEndTime] = useState('');

  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const isoCombine = (d, t) => {
    try {
      if (!d || !t) return null;
      return new Date(`${d}T${t}:00`).toISOString();
    } catch {
      return null;
    }
  };

  const overlap = (aStart, aEnd, bStart, bEnd) => {
    return (aStart < bEnd) && (bStart < aEnd);
  };

  const hasDuplicateRequest = (shiftId, startISO, endISO) => {
    return requests.some(r =>
      r.shift_id === Number(shiftId) &&
      r.start_time === startISO &&
      r.end_time === endISO
    );
  };

  const authHeaders = () => ({
    Authorization: `Bearer ${authToken}`,
    'Content-Type': 'application/json'
  });

  const fetchUsers = async () => {
    try {
      const r = await fetch(`${API_BASE}/users/`, { headers: authHeaders() });
      if (r.ok) {
        setUsers(await r.json());
      } else {
        // Fallback si endpoint non accessible: on utilise juste l'utilisateur courant
        if (user) {
          setUsers([user]);
        }
      }
    } catch {
      if (user) setUsers([user]);
    }
  };

  const fetchShifts = async () => {
    try {
      const r = await fetch(`${API_BASE}/shifts/`, { headers: authHeaders() });
      if (!r.ok) return;
      setShifts(await r.json());
    } catch {}
  };

  const fetchRequests = async () => {
    try {
      const r = await fetch(`${API_BASE}/requests/`, { headers: authHeaders() });
      if (!r.ok) return;
      setRequests(await r.json());
    } catch {}
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchShifts();
    fetchRequests();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Auto sélection si un seul utilisateur
  useEffect(() => {
    if (users.length === 1 && users[0]?.id) {
      setShiftUserId(String(users[0].id));
    }
  }, [users]);

  const resetMessages = () => {
    setMsg(null);
    setErr(null);
  };

  const handleCreateShift = async (e) => {
    e.preventDefault();
    resetMessages();
    const start_time = isoCombine(shiftStartDate, shiftStartTime);
    const end_time = isoCombine(shiftEndDate, shiftEndTime);
    if (!start_time || !end_time || !shiftUserId) {
      setErr('Champs incomplets.');
      return;
    }
    if (new Date(start_time) >= new Date(end_time)) {
      setErr('Heures invalides: début >= fin.');
      return;
    }
    // Vérifier chevauchement local simple (optionnel)
    const startMs = new Date(start_time).getTime();
    const endMs = new Date(end_time).getTime();
    const conflict = shifts.find(s =>
      overlap(startMs, endMs, new Date(s.start_time).getTime(), new Date(s.end_time).getTime()) &&
      s.user_id === Number(shiftUserId)
    );
    if (conflict) {
      setErr(`Chevauchement avec shift #${conflict.id}.`);
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/shifts/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ start_time, end_time, user_id: Number(shiftUserId) })
      });
      if (!r.ok) {
        let detail = '';
        try { const j = await r.json(); detail = j.detail || JSON.stringify(j); } catch {}
        if (r.status === 409) setErr(`Conflit shift (409). ${detail}`);
        else if (r.status === 403) setErr('Action interdite (403).');
        else setErr(`Erreur création shift (${r.status}). ${detail}`);
      } else {
        setMsg('Shift créé.');
        setShiftUserId('');
        setShiftStartDate('');
        setShiftStartTime('');
        setShiftEndDate('');
        setShiftEndTime('');
        fetchShifts();
      }
    } catch {
      setErr('Erreur réseau.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    resetMessages();
    const start_time = isoCombine(reqStartDate, reqStartTime);
    const end_time = isoCombine(reqEndDate, reqEndTime);
    if (!start_time || !end_time || !requestShiftId) {
      setErr('Champs incomplets.');
      return;
    }
    if (new Date(start_time) >= new Date(end_time)) {
      setErr('Heures invalides: début >= fin.');
      return;
    }
    if (hasDuplicateRequest(requestShiftId, start_time, end_time)) {
      setErr('Cette request existe déjà.');
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/requests/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ shift_id: Number(requestShiftId), start_time, end_time })
      });
      if (!r.ok) {
        let detail = '';
        try { const j = await r.json(); detail = j.detail || JSON.stringify(j); } catch {}
        if (r.status === 409) setErr(`Conflit request (409). ${detail}`);
        else if (r.status === 403) setErr('Action interdite (403).');
        else setErr(`Erreur création request (${r.status}). ${detail}`);
      } else {
        setMsg('Request créée.');
        setRequestShiftId('');
        setReqStartDate('');
        setReqStartTime('');
        setReqEndDate('');
        setReqEndTime('');
        fetchRequests();
      }
    } catch {
      setErr('Erreur réseau.');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) return <div className="p-4">Authentification requise.</div>;

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-8">
      <h1 className="text-2xl font-semibold">Gestion Shifts & Requests</h1>

      {msg && <div className="text-green-600 text-sm">{msg}</div>}
      {err && <div className="text-red-600 text-sm">{err}</div>}

      <section className="border rounded-md p-4 space-y-4 bg-white">
        <h2 className="text-xl font-medium">Créer un Shift</h2>
        <form onSubmit={handleCreateShift} className="grid md:grid-cols-5 gap-3 items-end">
          {users.length > 1 && (
            <div className="flex flex-col">
              <label className="text-xs font-medium">Employé</label>
              <select
                className="border rounded px-2 py-1 text-sm"
                value={shiftUserId}
                onChange={(e) => setShiftUserId(e.target.value)}
              >
                <option value="">—</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
                ))}
              </select>
            </div>
          )}
          {users.length === 1 && (
            <div className="flex flex-col">
              <label className="text-xs font-medium">Employé</label>
              <input
                type="text"
                readOnly
                className="border rounded px-2 py-1 text-sm bg-gray-50"
                value={users[0].full_name || users[0].email}
              />
            </div>
          )}
          <div className="flex flex-col">
            <label className="text-xs font-medium">Début (date)</label>
            <input
              type="date"
              className="border rounded px-2 py-1 text-sm"
              value={shiftStartDate}
              onChange={(e) => setShiftStartDate(e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium">Début (heure)</label>
            <input
              type="time"
              className="border rounded px-2 py-1 text-sm"
              value={shiftStartTime}
              onChange={(e) => setShiftStartTime(e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium">Fin (date)</label>
            <input
              type="date"
              className="border rounded px-2 py-1 text-sm"
              value={shiftEndDate}
              onChange={(e) => setShiftEndDate(e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium">Fin (heure)</label>
            <input
              type="time"
              className="border rounded px-2 py-1 text-sm"
              value={shiftEndTime}
              onChange={(e) => setShiftEndTime(e.target.value)}
            />
          </div>
          <div className="md:col-span-5">
            <button
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded text-sm disabled:opacity-50"
            >
              {loading ? '...' : 'Créer Shift'}
            </button>
          </div>
        </form>
        <div>
          <h3 className="font-medium mb-2 text-sm">Shifts existants</h3>
          <div className="max-h-48 overflow-auto text-xs space-y-1">
            {shifts.map(s => (
              <div key={s.id} className="border rounded px-2 py-1 flex justify-between">
                <span>#{s.id} — U:{s.user_id}</span>
                <span>{new Date(s.start_time).toLocaleString()} → {new Date(s.end_time).toLocaleString()}</span>
              </div>
            ))}
            {!shifts.length && <div className="text-gray-500">Aucun shift.</div>}
          </div>
        </div>
      </section>

      <section className="border rounded-md p-4 space-y-4 bg-white">
        <h2 className="text-xl font-medium">Créer une Request</h2>
        <form onSubmit={handleCreateRequest} className="grid md:grid-cols-5 gap-3 items-end">
          <div className="flex flex-col md:col-span-2">
            <label className="text-xs font-medium">Shift concerné</label>
            <select
              className="border rounded px-2 py-1 text-sm"
              value={requestShiftId}
              onChange={(e) => setRequestShiftId(e.target.value)}
            >
              <option value="">—</option>
              {shifts.map(s => (
                <option key={s.id} value={s.id}>
                  #{s.id} {new Date(s.start_time).toLocaleDateString()} ({new Date(s.start_time).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}-{new Date(s.end_time).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})})
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium">Début (date)</label>
            <input
              type="date"
              className="border rounded px-2 py-1 text-sm"
              value={reqStartDate}
              onChange={(e) => setReqStartDate(e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium">Début (heure)</label>
            <input
              type="time"
              className="border rounded px-2 py-1 text-sm"
              value={reqStartTime}
              onChange={(e) => setReqStartTime(e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium">Fin (date)</label>
            <input
              type="date"
              className="border rounded px-2 py-1 text-sm"
              value={reqEndDate}
              onChange={(e) => setReqEndDate(e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium">Fin (heure)</label>
            <input
              type="time"
              className="border rounded px-2 py-1 text-sm"
              value={reqEndTime}
              onChange={(e) => setReqEndTime(e.target.value)}
            />
          </div>
          <div className="md:col-span-5">
            <button
              disabled={loading}
              className="px-4 py-2 bg-orange-600 text-white rounded text-sm disabled:opacity-50"
            >
              {loading ? '...' : 'Créer Request'}
            </button>
          </div>
        </form>
        <div>
          <h3 className="font-medium mb-2 text-sm">Requests</h3>
          <div className="max-h-48 overflow-auto text-xs space-y-1">
            {requests.map(r => (
              <div key={r.id} className="border rounded px-2 py-1 flex justify-between">
                <span>#{r.id} Shift:{r.shift_id}</span>
                <span>{r.status}</span>
              </div>
            ))}
            {!requests.length && <div className="text-gray-500">Aucune request.</div>}
          </div>
        </div>
      </section>

      <section className="border rounded-md p-4 bg-white">
        <h2 className="text-sm font-medium mb-2">Mes Requests (tous rôles)</h2>
        <div className="grid md:grid-cols-2 gap-2 text-xs">
          {requests.map(r => (
            <div key={r.id} className="border rounded p-2 space-y-1">
              <div>Request #{r.id} — shift #{r.shift_id}</div>
              <div>Status: {r.status}</div>
              <div>Début demandé: {new Date(r.start_time).toLocaleString()}</div>
              <div>Fin demandée: {new Date(r.end_time).toLocaleString()}</div>
            </div>
          ))}
          {!requests.length && <div className="text-gray-500">Aucune request.</div>}
        </div>
      </section>
    </div>
  );
}

export default AddShiftsRequests;
